/**
 * WQS Product Main
 * Handles: gallery thumbnail swap, live variant matching/price update,
 * option availability, meal-plan summary calculation, subscribe-card UX,
 * selling-plan (subscribe) toggle, line-item properties, and AJAX
 * add-to-cart + checkout redirect.
 * Supports multiple independent instances on the same page.
 */
(function () {
  function formatMoney(cents, moneyFormat) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var formattedNumber = withCommas + '.' + parts[1];
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, formattedNumber)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas);
  }

  function parseLeadingNumber(str) {
    var match = String(str || '').match(/\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : null;
  }

  function initGallery(root) {
    var stage = root.querySelector('[data-wqs-main-stage]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-wqs-thumb]'));
    if (!stage || !thumbs.length) return;

    var activeIndex = thumbs.findIndex(function (t) { return t.classList.contains('is-active'); });
    if (activeIndex === -1) activeIndex = 0;

    var isAnimating = false;

    function swapImage(thumb, direction) {
      var currentImg = stage.querySelector('img[data-wqs-main-image]');
      var src = thumb.getAttribute('data-full-src');
      if (!currentImg || !src) return;

      isAnimating = true;

      var newImg = document.createElement('img');
      newImg.setAttribute('data-wqs-main-image', '');
      newImg.src = src;
      var srcset = thumb.getAttribute('data-full-srcset');
      if (srcset) newImg.srcset = srcset;
      newImg.alt = thumb.getAttribute('data-full-alt') || '';
      var width = thumb.getAttribute('data-full-width');
      var height = thumb.getAttribute('data-full-height');
      if (width) newImg.setAttribute('width', width);
      if (height) newImg.setAttribute('height', height);

      newImg.classList.add(direction === 'right' ? 'wqs-slide-in-right' : 'wqs-slide-in-left');
      stage.appendChild(newImg);

      var aspectRatio = thumb.getAttribute('data-full-aspect-ratio');
      if (aspectRatio) stage.style.aspectRatio = aspectRatio;

      // Force layout so the browser registers the starting position
      // before we animate to the resting position.
      void newImg.offsetWidth;

      currentImg.classList.add('wqs-sliding');
      newImg.classList.add('wqs-sliding');

      requestAnimationFrame(function () {
        currentImg.classList.add(direction === 'right' ? 'wqs-slide-out-left' : 'wqs-slide-out-right');
        newImg.classList.remove('wqs-slide-in-right', 'wqs-slide-in-left');
      });

      var cleaned = false;
      function cleanup() {
        if (cleaned) return;
        cleaned = true;
        currentImg.remove();
        newImg.classList.remove('wqs-sliding');
        isAnimating = false;
      }

      newImg.addEventListener('transitionend', cleanup, { once: true });
      setTimeout(cleanup, 600);
    }

    thumbs.forEach(function (thumb, index) {
      thumb.addEventListener('click', function () {
        if (isAnimating || index === activeIndex) return;

        var direction = index > activeIndex ? 'right' : 'left';
        swapImage(thumb, direction);
        activeIndex = index;

        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
      });
    });
  }

  function initVariants(root, sectionId) {
    var variants = [];
    try {
      var jsonEl = document.querySelector('script[data-wqs-variants="' + sectionId + '"]');
      if (jsonEl) variants = JSON.parse(jsonEl.textContent);
    } catch (e) {
      variants = [];
    }

    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    var optionSelects = Array.prototype.slice.call(root.querySelectorAll('[data-wqs-option-select]'));
    var priceEl = root.querySelector('[data-wqs-price]');
    var variantIdInput = root.querySelector('[data-wqs-variant-id]');
    var submitBtn = root.querySelector('[data-wqs-submit]');
    var submitTextEl = root.querySelector('[data-wqs-submit-text]');
    var buttonPriceEl = root.querySelector('[data-wqs-button-price]');
    var buttonText = root.getAttribute('data-button-text') || 'Add to cart';
    var soldOutText = root.getAttribute('data-sold-out-text') || 'Sold out';

    var purchaseRadios = Array.prototype.slice.call(root.querySelectorAll('[data-wqs-purchase-radio]'));
    var subscribeCards = Array.prototype.slice.call(root.querySelectorAll('[data-wqs-subscribe-card]'));
    var sellingPlanInput = root.querySelector('[data-wqs-selling-plan]');
    var subscribeBadge = root.querySelector('[data-wqs-subscribe-badge]');
    var badgeTemplate = root.getAttribute('data-badge-template') || 'Save [percent]%';

    var summaryEl = root.querySelector('[data-wqs-summary]');
    var planUnavailableEl = root.querySelector('[data-wqs-plan-unavailable]');
    var summaryMealsEl = root.querySelector('[data-wqs-summary-meals]');
    var summaryBreakdownEl = root.querySelector('[data-wqs-summary-breakdown]');
    var summaryTotalEl = root.querySelector('[data-wqs-summary-total]');
    var summaryPerMealEl = root.querySelector('[data-wqs-summary-per-meal]');
    var summaryMealsSuffix = root.getAttribute('data-summary-meals-suffix') || 'Meals per week';
    var summaryPerDayWord = root.getAttribute('data-summary-per-day-word') || 'meals per day';
    var summaryPerWeekWord = root.getAttribute('data-summary-per-week-word') || 'days per week';
    var summaryPerMealLabel = root.getAttribute('data-summary-per-meal-label') || 'per meal';

    var propertyInputs = Array.prototype.slice.call(root.querySelectorAll('[data-wqs-option-property]'));
    var mealsPropertyInput = root.querySelector('[data-wqs-meals-property]');

    function getSelectedOptionValues() {
      return optionSelects
        .slice()
        .sort(function (a, b) { return Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex); })
        .map(function (select) { return select.value; });
    }

    function isSelectionComplete() {
      if (optionSelects.length === 0) return true;
      return getSelectedOptionValues().every(function (val) { return val !== ''; });
    }

    function findMatchingVariant() {
      var selected = getSelectedOptionValues();
      return variants.find(function (variant) {
        return selected.every(function (val, idx) {
          var key = 'option' + (idx + 1);
          return variant[key] === val;
        });
      });
    }

    function updateOptionAvailability() {
      var current = getSelectedOptionValues();
      optionSelects.forEach(function (select) {
        var idx = Number(select.dataset.optionIndex);
        var wrap = select.closest('[data-wqs-custom-select]');
        var menu = wrap ? wrap.querySelector('[data-wqs-select-menu]') : null;

        Array.prototype.forEach.call(select.options, function (opt) {
          if (opt.value === '') return;

          var testValues = current.slice();
          testValues[idx] = opt.value;
          var matches = variants.some(function (variant) {
            return testValues.every(function (val, i) {
              // An unselected dropdown (blank) is a wildcard, not a literal
              // value to match — only compare positions the customer has
              // actually chosen so far.
              if (val === '') return true;
              var key = 'option' + (i + 1);
              return variant[key] === val;
            });
          });
          opt.disabled = !matches;

          if (menu) {
            var li = menu.querySelector('[data-wqs-select-option][data-value="' + CSS.escape(opt.value) + '"]');
            if (li) li.classList.toggle('is-disabled', !matches);
          }
        });
      });
    }

    function updatePropertyInputs(selected, mealsPerWeek) {
      propertyInputs.forEach(function (input) {
        var idx = Number(input.dataset.optionIndex);
        input.value = selected[idx] || '';
      });
      if (mealsPropertyInput) {
        mealsPropertyInput.value = mealsPerWeek !== null ? String(mealsPerWeek) : '';
      }
    }

    function isSubscribing() {
      var subscribeRadio = purchaseRadios.find(function (r) { return r.value === 'subscribe'; });
      return !!(subscribeRadio && subscribeRadio.checked);
    }

    // The price actually charged: the Recharge-discounted selling-plan
    // price when "Subscribe & Save" is selected (and the plan has one),
    // otherwise the plain variant price. Every displayed price (main
    // price, plan summary, button) is derived from this single source so
    // toggling subscribe/one-time always updates them together.
    function getActivePrice(variant) {
      if (!variant) return null;
      try {
        if (isSubscribing()) {
          var allocations = variant.selling_plan_allocations || [];
          if (allocations.length && typeof allocations[0].price === 'number') {
            return allocations[0].price;
          }
        }
      } catch (e) {
        // Fall through to the plain variant price below — a malformed or
        // unexpected selling-plan data shape should never take down price
        // display for the whole card.
      }
      return variant.price;
    }

    function updatePlanSummary(variant, selected, activePrice) {
      if (!summaryEl) return null;

      var numbers = selected.map(parseLeadingNumber);
      var allNumeric = numbers.length >= 2 && numbers.every(function (n) { return n !== null; });

      if (!variant || activePrice === null || !allNumeric) {
        summaryEl.hidden = true;
        return null;
      }

      var mealsPerWeek = numbers.reduce(function (a, b) { return a * b; }, 1);

      if (summaryMealsEl) summaryMealsEl.textContent = mealsPerWeek + ' ' + summaryMealsSuffix;

      if (summaryBreakdownEl) {
        if (numbers.length >= 3) {
          summaryBreakdownEl.textContent =
            numbers[1] + ' ' + summaryPerDayWord + ' × ' + numbers[2] + ' ' + summaryPerWeekWord;
        } else {
          summaryBreakdownEl.textContent = selected.join(' × ');
        }
      }

      if (summaryTotalEl) summaryTotalEl.textContent = formatMoney(activePrice, moneyFormat);

      if (summaryPerMealEl && mealsPerWeek > 0) {
        var perMealCents = Math.round(activePrice / mealsPerWeek);
        summaryPerMealEl.textContent = formatMoney(perMealCents, moneyFormat) + ' ' + summaryPerMealLabel;
      }

      summaryEl.hidden = false;
      return mealsPerWeek;
    }

    function updateSubscribeBadge(variant) {
      if (!subscribeBadge) return;
      try {
        var allocations = (variant && variant.selling_plan_allocations) || [];
        var allocation = allocations[0];

        if (!allocation || !allocation.compare_at_price || allocation.compare_at_price <= allocation.price) {
          subscribeBadge.hidden = true;
          return;
        }

        var percent = Math.round(((allocation.compare_at_price - allocation.price) / allocation.compare_at_price) * 100);
        if (percent <= 0) {
          subscribeBadge.hidden = true;
          return;
        }

        subscribeBadge.textContent = badgeTemplate.replace('[percent]', percent);
        subscribeBadge.hidden = false;
      } catch (e) {
        subscribeBadge.hidden = true;
      }
    }

    function updateSubscribeCardState() {
      subscribeCards.forEach(function (card) {
        var radio = card.querySelector('[data-wqs-purchase-radio]');
        card.classList.toggle('is-selected', !!(radio && radio.checked));
      });
    }

    function updateSellingPlanForCurrentVariant(variant) {
      if (!sellingPlanInput) return;

      if (!isSubscribing()) {
        sellingPlanInput.value = '';
        return;
      }

      try {
        var allocations = (variant && variant.selling_plan_allocations) || [];
        var planId = allocations.length ? allocations[0].selling_plan_id : '';
        sellingPlanInput.value = planId || planId === 0 ? String(planId) : '';
      } catch (e) {
        // Never let an unexpected selling-plan data shape leave a stale
        // plan ID sitting in the form — clear it so a one-time purchase
        // is submitted rather than a wrong/broken subscription.
        sellingPlanInput.value = '';
      }
    }

    function updateButtonPrice(activePrice) {
      if (!buttonPriceEl) return;
      if (activePrice === null) {
        buttonPriceEl.textContent = '';
        return;
      }
      buttonPriceEl.textContent = ' • ' + formatMoney(activePrice, moneyFormat);
    }

    // Single source of truth for the whole right-hand column. Called on
    // every dropdown change AND every subscribe/one-time toggle change, so
    // the two controls always stay in sync — never update one without the
    // other.
    function updateUI(variant) {
      updateOptionAvailability();
      updateSubscribeCardState();

      var selected = getSelectedOptionValues();
      var complete = isSelectionComplete();
      var activePrice = complete ? getActivePrice(variant) : null;

      if (planUnavailableEl) {
        // Only show this when every dropdown has a value but no variant
        // matches that exact combination — distinct from "still choosing".
        planUnavailableEl.hidden = !(complete && !variant);
      }

      if (!complete || !variant) {
        if (priceEl) {
          priceEl.hidden = true;
          priceEl.textContent = '';
        }
        updateSellingPlanForCurrentVariant(null);
        updateSubscribeBadge(null);
        updatePlanSummary(null, selected, null);
        updateButtonPrice(null);
        updatePropertyInputs(selected, null);
        if (submitBtn) {
          submitBtn.disabled = true;
          if (submitTextEl) submitTextEl.textContent = buttonText;
        }
        return;
      }

      if (variantIdInput) variantIdInput.value = variant.id;
      if (priceEl) {
        priceEl.textContent = formatMoney(activePrice, moneyFormat);
        priceEl.hidden = false;
      }

      updateSellingPlanForCurrentVariant(variant);
      updateSubscribeBadge(variant);
      var mealsPerWeek = updatePlanSummary(variant, selected, activePrice);
      updateButtonPrice(activePrice);
      updatePropertyInputs(selected, mealsPerWeek);

      if (submitBtn) {
        if (variant.available) {
          submitBtn.disabled = false;
          if (submitTextEl) submitTextEl.textContent = buttonText;
        } else {
          submitBtn.disabled = true;
          if (submitTextEl) submitTextEl.textContent = soldOutText;
        }
      }
    }

    optionSelects.forEach(function (select) {
      select.addEventListener('change', function () {
        var variant = findMatchingVariant();
        updateUI(variant);
      });
    });

    // Note: subscribe cards are native <label> elements wrapping their
    // radio input, so clicking anywhere on a card already toggles it —
    // no extra click handler needed here.

    purchaseRadios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        // Route through the same updateUI() the dropdowns use, so
        // switching Subscribe/One-time refreshes price, summary, and
        // button together with the current meal-plan selection — the two
        // controls never go out of sync with each other.
        updateUI(findMatchingVariant());
      });
    });

    // Initial pass: reveals price immediately for zero-option products,
    // keeps it hidden otherwise until every dropdown has a real value.
    updateUI(findMatchingVariant());
  }

  function initForm(root, sectionId) {
    var form = root.querySelector('[data-wqs-form]');
    var submitBtn = root.querySelector('[data-wqs-submit]');
    var submitTextEl = root.querySelector('[data-wqs-submit-text]');
    var messageEl = root.querySelector('[data-wqs-message]');
    var buttonText = root.getAttribute('data-button-text') || 'Add to cart';
    var addingText = root.getAttribute('data-adding-text') || 'Adding...';
    var successText = root.getAttribute('data-success-text') || 'Added to cart';
    var checkoutUrl = root.getAttribute('data-checkout-url') || '/checkout';

    if (!form) return;

    // Explicit lock, independent of the button's own disabled state, so a
    // submit can never fire twice for the same click — even if the
    // "submit" listener were ever accidentally bound more than once.
    var isSubmitting = false;

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (isSubmitting || submitBtn.disabled) return;
      isSubmitting = true;

      if (messageEl) {
        messageEl.hidden = true;
        messageEl.removeAttribute('data-state');
        messageEl.textContent = '';
      }

      submitBtn.classList.add('is-loading');
      submitBtn.disabled = true;
      if (submitTextEl) submitTextEl.textContent = addingText;

      var formData = new FormData(form);

      // Hard guarantee against duplicate line-item properties (or any
      // other duplicated field): collapse every field name down to a
      // single value before sending, no matter how many inputs with that
      // name exist in the form or what put them there. FormData.set()
      // replaces *all* existing entries for a key with just this one, and
      // calling it again for a later duplicate overwrites again — so the
      // last (most current) occurrence in the DOM always wins.
      Array.prototype.forEach.call(form.querySelectorAll('[name]'), function (field) {
        var name = field.getAttribute('name');
        if (!name) return;
        formData.set(name, field.value);
      });

      var sellingPlanField = form.querySelector('[data-wqs-selling-plan]');
      if (sellingPlanField && !sellingPlanField.value) {
        formData.delete('selling_plan');
      }

      fetch(form.getAttribute('action') + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw data;
            return data;
          });
        })
        .then(function () {
          if (messageEl) {
            messageEl.hidden = false;
            messageEl.setAttribute('data-state', 'success');
            messageEl.textContent = successText;
          }
          window.location.href = checkoutUrl;
        })
        .catch(function (err) {
          isSubmitting = false;
          submitBtn.classList.remove('is-loading');
          submitBtn.disabled = false;
          if (submitTextEl) submitTextEl.textContent = buttonText;
          if (messageEl) {
            messageEl.hidden = false;
            messageEl.setAttribute('data-state', 'error');
            messageEl.textContent = (err && err.description) || (err && err.message) || 'Something went wrong. Please try again.';
          }
        });
    });
  }

  function initCustomSelects(root) {
    var wraps = Array.prototype.slice.call(root.querySelectorAll('[data-wqs-custom-select]'));

    function closeWrap(wrap) {
      var trigger = wrap.querySelector('[data-wqs-select-trigger]');
      var menu = wrap.querySelector('[data-wqs-select-menu]');
      wrap.removeAttribute('data-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (menu) menu.hidden = true;
    }

    function closeAll(except) {
      wraps.forEach(function (wrap) {
        if (wrap !== except) closeWrap(wrap);
      });
    }

    wraps.forEach(function (wrap) {
      var trigger = wrap.querySelector('[data-wqs-select-trigger]');
      var menu = wrap.querySelector('[data-wqs-select-menu]');
      var valueEl = wrap.querySelector('[data-wqs-select-value]');
      var nativeSelect = wrap.querySelector('[data-wqs-option-select]');
      var options = Array.prototype.slice.call(wrap.querySelectorAll('[data-wqs-select-option]'));

      if (!trigger || !menu || !nativeSelect) return;

      trigger.addEventListener('click', function () {
        var isOpen = wrap.getAttribute('data-open') === 'true';
        closeAll(wrap);
        if (isOpen) {
          closeWrap(wrap);
        } else {
          wrap.setAttribute('data-open', 'true');
          trigger.setAttribute('aria-expanded', 'true');
          menu.hidden = false;
        }
      });

      options.forEach(function (option) {
        option.addEventListener('click', function () {
          if (option.classList.contains('is-disabled')) return;

          var value = option.getAttribute('data-value');
          nativeSelect.value = value;
          nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));

          options.forEach(function (opt) {
            opt.classList.remove('is-selected');
            opt.setAttribute('aria-selected', 'false');
          });
          option.classList.add('is-selected');
          option.setAttribute('aria-selected', 'true');
          if (valueEl) valueEl.textContent = value;

          closeWrap(wrap);
          trigger.focus();
        });
      });

      trigger.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closeWrap(wrap);
      });
    });

    document.addEventListener('click', function (event) {
      wraps.forEach(function (wrap) {
        if (!wrap.contains(event.target)) closeWrap(wrap);
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeAll();
    });
  }

  function initRoot(root) {
    if (root.dataset.wqsInitialized) return;
    root.dataset.wqsInitialized = 'true';

    var sectionId = root.getAttribute('data-section-id');
    initGallery(root);
    initCustomSelects(root);
    initVariants(root, sectionId);
    initForm(root, sectionId);
  }

  function init() {
    var roots = document.querySelectorAll('[data-wqs-root]');
    Array.prototype.forEach.call(roots, initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-wqs-root]');
    if (root) initRoot(root);
  });
})();
